import React from 'react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material'

interface BlockConfirmDialogProps {
  open: boolean
  contactName?: string
  allowIdentity: boolean
  loading?: boolean
  defaultTarget?: 'identity' | 'contact'
  onClose: () => void
  onBlockIdentity: (reason: string) => Promise<void>
  onBlockContact: (reason: string) => Promise<void>
}

const BlockConfirmDialog: React.FC<BlockConfirmDialogProps> = ({
  open,
  contactName,
  allowIdentity,
  loading = false,
  defaultTarget = 'identity',
  onClose,
  onBlockIdentity,
  onBlockContact,
}) => {
  const [target, setTarget] = React.useState<'identity' | 'contact'>(allowIdentity ? defaultTarget : 'contact')
  const [reason, setReason] = React.useState('')
  const [submitting, setSubmitting] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (open) {
      setTarget(allowIdentity ? defaultTarget : 'contact')
      setReason('')
      setError(null)
    }
  }, [open, allowIdentity, defaultTarget])

  const handleSubmit = async () => {
    setSubmitting(true)
    setError(null)

    try {
      if (target === 'identity' && allowIdentity) {
        await onBlockIdentity(reason)
      } else {
        await onBlockContact(reason)
      }
      onClose()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to update blocklist.'
      setError(message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Block contact</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Blocking prevents replies and hides future messages from {contactName || 'this contact'}.
          </Typography>
          <FormControl component="fieldset">
            <FormLabel component="legend">Block target</FormLabel>
            <RadioGroup
              value={target}
              onChange={(event) => setTarget(event.target.value as 'identity' | 'contact')}
            >
              {allowIdentity && (
                <FormControlLabel value="identity" control={<Radio />} label="Only this identity" />
              )}
              <FormControlLabel value="contact" control={<Radio />} label="Entire contact" />
            </RadioGroup>
          </FormControl>
          <TextField
            label="Reason (optional)"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            multiline
            minRows={2}
            placeholder="Reason for blocking"
          />
          {error && (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={submitting || loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="warning"
          disabled={submitting || loading || (!allowIdentity && target === 'identity')}
        >
          {submitting ? 'Blocking…' : 'Confirm block'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default BlockConfirmDialog

